import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ThumbsUp, ThumbsDown, Minus, Lightbulb, Check } from 'lucide-react';
import { useAuthStore, useLearningStore } from '@shared/state';
import { submitFeedback } from '@shared/services/feedback';
import { loadProfile } from '@shared/services/profile';
import { trackEvent, getBaseProperties } from '@shared/services/analytics';
import { ANALYTICS_EVENTS } from '@shared/constants/analytics-events';
import type { FeedbackType, FeedbackSentiment, FeedbackContext } from '@shared/types/feedback';
import { Button } from '../ui/Button';
import { useFeedbackPanelStore } from './feedbackPanelStore';

interface TypeOption {
  value: FeedbackType;
  label: string;
}

const TYPE_OPTIONS: TypeOption[] = [
  { value: 'general', label: 'General' },
  { value: 'content_quality', label: 'Content' },
  { value: 'profile_accuracy', label: 'Profile' },
  { value: 'difficulty', label: 'Difficulty' },
  { value: 'format_preference', label: 'Format' },
];

interface SentimentOption {
  value: FeedbackSentiment;
  label: string;
  icon: React.ReactNode;
  activeColour: string;
}

const SENTIMENT_OPTIONS: SentimentOption[] = [
  { value: 'positive', label: 'Positive', icon: <ThumbsUp size={14} />, activeColour: '#55EFC4' },
  { value: 'negative', label: 'Negative', icon: <ThumbsDown size={14} />, activeColour: '#FD79A8' },
  { value: 'neutral', label: 'Neutral', icon: <Minus size={14} />, activeColour: '#F0A500' },
  { value: 'suggestion', label: 'Suggestion', icon: <Lightbulb size={14} />, activeColour: '#6C5CE7' },
];

function pageNameFromPath(pathname: string): string {
  if (pathname === '/' || pathname === '') return 'landing';
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] === 'learn') return 'learning';
  return segments[0] ?? 'unknown';
}

export function FeedbackPanel() {
  const open = useFeedbackPanelStore((s) => s.open);
  const closePanel = useFeedbackPanelStore((s) => s.closePanel);
  const user = useAuthStore((s) => s.user);
  const currentPath = useLearningStore((s) => s.currentPath);
  const currentNodeId = useLearningStore((s) => s.currentNodeId);
  const location = useLocation();

  const [feedbackType, setFeedbackType] = useState<FeedbackType>('general');
  const [sentiment, setSentiment] = useState<FeedbackSentiment>('neutral');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [profileVersion, setProfileVersion] = useState<number>(1);

  useEffect(() => {
    if (!open) return;
    setFeedbackType('general');
    setSentiment('neutral');
    setMessage('');
    setSuccess(false);
    setSubmitting(false);
  }, [open]);

  useEffect(() => {
    if (!user) return;
    loadProfile(user.uid).then((p) => {
      if (p) setProfileVersion(p.profileVersion);
    }).catch(() => { /* ignore */ });
  }, [user]);

  const context: FeedbackContext = useMemo(() => ({
    pathId: currentPath?.pathId ?? null,
    nodeId: currentNodeId,
    contentId: currentPath?.contentId ?? null,
    conceptId: null,
    currentTier: user?.subscription ?? 'free',
    profileVersion,
    pageName: pageNameFromPath(location.pathname),
  }), [currentPath, currentNodeId, user, profileVersion, location.pathname]);

  async function handleSubmit() {
    if (!user || message.trim().length === 0 || submitting) return;
    setSubmitting(true);
    try {
      const entry = await submitFeedback({
        userId: user.uid,
        feedbackType,
        message: message.trim(),
        sentiment,
        context,
      });

      await trackEvent({
        name: ANALYTICS_EVENTS.FEEDBACK_SUBMITTED,
        properties: {
          ...getBaseProperties(),
          tier: (user.subscription ?? 'free') as 'free' | 'learner' | 'pro' | 'teams',
          feedbackId: entry.feedbackId,
          feedbackType,
          sentiment,
          hasContext: context.pathId !== null || context.nodeId !== null,
          messageLength: message.trim().length,
          pathId: context.pathId,
          nodeId: context.nodeId,
          pageName: context.pageName,
        },
      });

      setSuccess(true);
      setTimeout(() => {
        closePanel();
      }, 1600);
    } catch (err) {
      console.error('Feedback submit failed:', err);
      setSubmitting(false);
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closePanel}
          />

          <motion.div
            className="fixed bottom-4 left-4 sm:bottom-6 sm:left-6 z-50 w-[calc(100%-2rem)] sm:w-[380px]"
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <div
              className="rounded-xl border shadow-2xl"
              style={{ background: '#181D35', borderColor: '#2A2F4F' }}
            >
              {success ? (
                <div className="p-8 text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(85,239,196,0.15)' }}
                  >
                    <Check size={24} className="text-[#55EFC4]" />
                  </motion.div>
                  <p className="font-heading font-semibold text-sm mb-1">Thanks</p>
                  <p className="text-xs text-content-secondary font-body">
                    This helps CogniPrint learn about you.
                  </p>
                </div>
              ) : (
                <>
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: '#2A2F4F' }}>
                    <h3 className="font-heading font-semibold text-sm">Send feedback</h3>
                    <button
                      type="button"
                      onClick={closePanel}
                      className="p-1 rounded-full text-content-muted hover:text-content hover:bg-dark-hover transition-colors cursor-pointer"
                      aria-label="Close"
                    >
                      <X size={16} />
                    </button>
                  </div>

                  {/* Body */}
                  <div className="p-4 space-y-4">
                    {/* Type pills */}
                    <div>
                      <p className="text-[10px] text-content-dim font-body uppercase tracking-wider mb-2">Topic</p>
                      <div className="flex flex-wrap gap-1.5">
                        {TYPE_OPTIONS.map((opt) => {
                          const isActive = feedbackType === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setFeedbackType(opt.value)}
                              className="px-2.5 py-1 rounded-full text-[11px] font-body transition-colors border cursor-pointer"
                              style={{
                                background: isActive ? 'rgba(108,92,231,0.18)' : 'transparent',
                                borderColor: isActive ? 'rgba(108,92,231,0.6)' : '#2A2F4F',
                                color: isActive ? '#A594F9' : '#9CA3AF',
                              }}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Sentiment */}
                    <div>
                      <p className="text-[10px] text-content-dim font-body uppercase tracking-wider mb-2">How do you feel?</p>
                      <div className="flex gap-1.5">
                        {SENTIMENT_OPTIONS.map((opt) => {
                          const isActive = sentiment === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setSentiment(opt.value)}
                              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-[11px] font-body cursor-pointer transition-colors"
                              style={{
                                background: isActive ? `${opt.activeColour}1f` : 'transparent',
                                borderColor: isActive ? opt.activeColour : '#2A2F4F',
                                color: isActive ? opt.activeColour : '#9CA3AF',
                              }}
                              aria-label={opt.label}
                            >
                              {opt.icon}
                              <span className="hidden sm:inline">{opt.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Tell us what's on your mind..."
                        rows={4}
                        maxLength={1000}
                        className="w-full px-3 py-2 rounded-lg text-xs text-content font-body placeholder:text-content-dim resize-none focus:outline-none focus:ring-1 focus:ring-indigo-500/50 focus:border-indigo-500 border"
                        style={{ background: '#111528', borderColor: '#2A2F4F' }}
                      />
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-[10px] text-content-dim font-body">
                          {context.pageName !== 'unknown' && `from ${context.pageName}`}
                        </span>
                        <span className="text-[10px] text-content-dim font-body">
                          {message.length}/1000
                        </span>
                      </div>
                    </div>

                    {/* Submit */}
                    <Button
                      className="w-full"
                      size="sm"
                      onClick={handleSubmit}
                      disabled={message.trim().length === 0 || submitting}
                    >
                      {submitting ? 'Sending...' : 'Send feedback'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
